import { useRef, useState, useEffect, ChangeEvent } from "react";
import { message } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomInputField from "../../../../../../../CustomHTMLElements/CustomInputField";
import { AllNGOBusinessSteps } from "../../..";
import useAllGender from "../../../../../../../../custom-hooks/useAllGender";
import useWorldCountries from "../../../../../../../../custom-hooks/useWorldCountries";
import useCountryStates from "../../../../../../../../custom-hooks/useCountryStates";
import { useIdentityVerificationTypes } from "../../../../../hooks/useIdentityVerificationTypes";
import useUtilityBillTypes from "../../../../../../../../custom-hooks/useUtilityBillTypes";
import useEducationalQualifications from "../../../../../hooks/useEducationalQualifications";
import {
  postFormDataWithDotNet,
  putFormDataWithDotNet,
} from "../../../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../../../api/ajax-endpoints";
import ErrorHandler from "../../../../../../../ErrorHandler/ErrorHandler.component";
import {
  DocumentType,
  CustomerPeopleTypes,
  BusinessTypes,
} from "../../../../../../../../interfaces/businessSignUp";
import useCustomerPeopleDetails, {
  TPeopleDetails,
} from "../../../../../hooks/useCustomerPeopleDetails";
import BusinessOwnersList from "../../../../../Components/BusinessOwnersList";
import Loader from "../../../../../../../../NewComponents/Loader/Loader.component";
import { ICorporateCustomer } from "../../../../../hooks/useCorporateCustomers";
import { errorHandler } from "../../../../../../../../helpers/errorHandler";
import { getMaxDateOfBirth } from "../../../../../../../../helpers/validators";

import styles from "../../../NGO.module.scss";

const schema = yup.object().shape({
  firstName: yup.string().required("Please enter first name").min(1).max(128),
  lastName: yup.string().required("Please enter last name").min(1).max(128),
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Please enter email address"),
  phoneNumber: yup
    .string()
    .required("Please enter phone number")
    .min(10, "Phone number must be at least 10 digits")
    .matches(/^\d+$/, "Must contain only numbers"),
  dateOfBirth: yup.string().required("Please select date of birth"),
  gender: yup.string().required("Please select gender"),
  countryCode: yup.string().required("Please select country"),
  state: yup.string().required("Please select state"),
  city: yup.string().required("Please enter city").min(1).max(128),
  address: yup
    .string()
    .required("Please enter residential address")
    .min(1)
    .max(128),
  identityNumber: yup.string().required("Please enter BVN"),
  govtIdentityType: yup.string().required("Please select identification type"),
  utilityDocumentType: yup.string().required("Please select utility bill type"),
  idDocument: yup.string().required("Please upload ID document"),
  utilityBill: yup.string().required("Please upload proof of address"),
  educationalQualification: yup
    .string()
    .required("Please select educational qualification"),
  workExperience: yup
    .string()
    .required("Please enter work experience")
    .min(1)
    .max(128),
  experienceRelevantToTheRole: yup
    .string()
    .required("Please enter experience relevant to role")
    .min(1)
    .max(128),
  periodInCompanyBoard: yup
    .string()
    .required("Please enter period in company board")
    .min(1)
    .max(128)
    .matches(/^\d+$/, "Must contain only numbers"),
});

interface IAddRegisteredTrustee {
  handleProceed: (step: number) => void;
  resumeCustomerData: ICorporateCustomer;
  formData: any;
}

const AddRegisteredTrustee: React.FC<IAddRegisteredTrustee> = ({
  handleProceed,
  resumeCustomerData,
  formData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [idDocumentFile, setIdDocumentFile] = useState<File | undefined>();
  const [utilityBillFile, setUtilityBillFile] = useState<File | undefined>();
  const [isPickingDocument, setIsPickingDocument] = useState(false);
  const [selectedBusinessOwner, setSelectedBusinessOwner] =
    useState<TPeopleDetails>();

  const formRef = useRef<HTMLFormElement>(null);
  const idDocumentRef = useRef<HTMLInputElement>(null);
  const utilityBillRef = useRef<HTMLInputElement>(null);

  const { data: countriesData, isLoading: isLoadingCountries } =
    useWorldCountries(1);
  const { data: gendersData } = useAllGender(selectedCountryCode || "NG");
  const { data: identityTypesData } = useIdentityVerificationTypes();
  const { data: utilityBillTypesData, isLoading: isLoadingUtilityBillTypes } =
    useUtilityBillTypes();
  const {
    data: educationalQualificationsData,
    isLoading: isLoadingEducationalQualifications,
  } = useEducationalQualifications();

  // Hook to fetch registered trustees
  const {
    data: customerPeopleDetails,
    refetch: refetchCustomerPeopleDetails,
    isLoading: isLoadingCustomerPeopleDetails,
  } = useCustomerPeopleDetails({
    customerId:
      formData?.customerId ||
      formData?.funder_id ||
      resumeCustomerData?.customer_id,
    funderPeopleType: CustomerPeopleTypes.REGISTERED_TRUSTEE,
  });

  const countries = countriesData?.data || [];
  const genders = gendersData?.data || [];
  const identityTypes = identityTypesData?.data || [];
  const utilityBillTypes = utilityBillTypesData?.data || [];
  const educationalQualifications =
    educationalQualificationsData?.data?.data || [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchCountry = watch("countryCode");

  // Get states for selected country
  const selectedCountryObject = countries.find(
    (c: any) => c.iso2 === watchCountry
  );
  const { data: statesData, isLoading: isLoadingStates } = useCountryStates(
    selectedCountryObject?.id || 0
  );
  const states = statesData?.data || [];

  useEffect(() => {
    if (watchCountry) {
      const country = countries.find((c: any) => c.iso2 === watchCountry);
      if (country) {
        setSelectedCountryCode(country.iso2 || "NG");
        setValue("state", "");
      }
    }
  }, [watchCountry, countries, setValue]);

  // Populate form when selectedBusinessOwner changes
  useEffect(() => {
    if (selectedBusinessOwner && selectedBusinessOwner.id) {
      setValue("firstName", selectedBusinessOwner.first_name || "");
      setValue("lastName", selectedBusinessOwner.last_name || "");
      setValue("email", selectedBusinessOwner.email || "");
      setValue("phoneNumber", selectedBusinessOwner.phone_number || "");
      setValue(
        "dateOfBirth",
        selectedBusinessOwner.date_of_birth?.split("T")[0] || ""
      );
      setValue("gender", selectedBusinessOwner.gender || "");
      setValue("countryCode", selectedBusinessOwner.country_code || "");
      setValue("state", selectedBusinessOwner.state || "");
      setValue("city", selectedBusinessOwner.city || "");
      setValue("address", selectedBusinessOwner.address || "");
      setValue("identityNumber", selectedBusinessOwner.nin || "");
      setValue(
        "govtIdentityType",
        selectedBusinessOwner.govt_identity_type_id?.toString() || ""
      );
      setTimeout(() => {
        setValue(
          "utilityDocumentType",
          selectedBusinessOwner.utility_bill_type_id?.toString() || ""
        );
      }, 2000);
      setValue(
        "educationalQualification",
        selectedBusinessOwner.educational_qualification || ""
      );
      setValue("workExperience", selectedBusinessOwner.work_experience || "");
      setValue(
        "experienceRelevantToTheRole",
        selectedBusinessOwner.experience_relevant_to_role || ""
      );
      setValue(
        "periodInCompanyBoard",
        selectedBusinessOwner.period_in_company_board?.toString() || ""
      );
    }
  }, [selectedBusinessOwner, setValue]);

  const onChangeFile = async (
    e: ChangeEvent<HTMLInputElement>,
    fileType: "idDocument" | "utilityBill"
  ) => {
    if (isPickingDocument) return;

    setIsPickingDocument(true);

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setIsPickingDocument(false);
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        message.error("File size must be less than 5MB");
        setIsPickingDocument(false);
        return;
      }

      const allowedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        message.error("Only PNG, JPG, JPEG, and PDF files are allowed");
        setIsPickingDocument(false);
        return;
      }

      if (fileType === "idDocument") {
        setIdDocumentFile(file);
        setValue("idDocument", file.name);
      } else {
        setUtilityBillFile(file);
        setValue("utilityBill", file.name);
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to upload file");
    } finally {
      setIsPickingDocument(false);
    }
  };

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);

    try {
      const form = new FormData();
      form.append(
        "OwnersInfo[0].FunderPersonType",
        CustomerPeopleTypes.REGISTERED_TRUSTEE.toString()
      );

      // If updating, include the ID
      if (selectedBusinessOwner?.id) {
        form.append("OwnersInfo[0].Id", selectedBusinessOwner.id.toString());
      }

      form.append(
        "CustomerId",
        formData?.funder_id || resumeCustomerData?.customer_id
      );
      form.append("OwnersInfo[0].FirstName", values.firstName);
      form.append("OwnersInfo[0].LastName", values.lastName);
      form.append("OwnersInfo[0].Email", values.email);
      form.append("OwnersInfo[0].PhoneNumber", values.phoneNumber);
      form.append("OwnersInfo[0].Gender", values.gender);
      form.append("OwnersInfo[0].CountryCode", values.countryCode);
      form.append("OwnersInfo[0].City", values.city);
      form.append("OwnersInfo[0].Address", values.address);
      form.append("OwnersInfo[0].State", values.state);
      form.append("OwnersInfo[0].DateOfBirth", values.dateOfBirth);
      form.append("OwnersInfo[0].GovtIdentityType", values.govtIdentityType);
      form.append("OwnersInfo[0].UtilityBillType", values.utilityDocumentType);
      form.append("OwnersInfo[0].Nin", values.identityNumber);
      form.append(
        "OwnersInfo[0].EducationalQualification",
        values.educationalQualification
      );
      form.append("OwnersInfo[0].WorkExperience", values.workExperience);
      form.append(
        "OwnersInfo[0].ExperienceRelevantToRole",
        values.experienceRelevantToTheRole
      );
      form.append(
        "OwnersInfo[0].PeriodInCompanyBoard",
        values.periodInCompanyBoard
      );
      form.append(
        "OwnersInfo[0].BusinessType",
        formData?.customerSubType ||
          formData?.customer_sub_type_id ||
          BusinessTypes.NGO.toString()
      );
      form.append(
        "OwnersInfo[0].FunderPersonDocuments[0].DocumentType",
        DocumentType.GOVERNMENT_ISSUED_DOCUMENT.toString()
      );
      if (idDocumentFile) {
        form.append(
          "OwnersInfo[0].FunderPersonDocuments[0].Document",
          idDocumentFile
        );
      }
      form.append(
        "OwnersInfo[0].FunderPersonDocuments[1].DocumentType",
        DocumentType.PROOF_OF_ADDRESS.toString()
      );
      if (utilityBillFile) {
        form.append(
          "OwnersInfo[0].FunderPersonDocuments[1].Document",
          utilityBillFile
        );
      }

      let response;
      if (selectedBusinessOwner?.id) {
        // Update existing trustee
        response = await putFormDataWithDotNet(
          `${ajaxEndpoints.ADD_BUSINESS_OWNER_INFORMATION}?sourceTypeId=2`,
          form
        );
      } else {
        // Add new trustee
        response = await postFormDataWithDotNet(
          `${ajaxEndpoints.ADD_BUSINESS_OWNER_INFORMATION}?sourceTypeId=2`,
          form
        );
      }

      if (response?.statusCode === 200 || response?.status_code === 200) {
        message.success(
          response?.data?.message ||
            response?.message ||
            `Registered trustee ${selectedBusinessOwner?.id ? "updated" : "added"} successfully!`
        );

        // Refetch the list and reset form
        refetchCustomerPeopleDetails();
        setSelectedBusinessOwner(undefined);
        setIdDocumentFile(undefined);
        setUtilityBillFile(undefined);

        // Reset form values
        const form = document.querySelector("form");
        if (form) form.reset();
      }
    } catch (error: any) {
      message.error(
        errorHandler(error) ||
          `Failed to ${selectedBusinessOwner?.id ? "update" : "save"} registered trustee information`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const handleNext = () => {
    handleProceed(AllNGOBusinessSteps.BUSINESS_DOCUMENTS);
  };

  const handleAddAnother = () => {
    setSelectedBusinessOwner(undefined);
    setIdDocumentFile(undefined);
    setUtilityBillFile(undefined);

    // Reset form
    const form = document.querySelector("form");
    if (form) form.reset();
  };

  return (
    <div>
      {/* Display list of registered trustees */}
      {isLoadingCustomerPeopleDetails ? (
        <Loader />
      ) : (
        <>
          {customerPeopleDetails &&
            customerPeopleDetails?.data &&
            customerPeopleDetails.data.length > 0 && (
              <BusinessOwnersList
                list={customerPeopleDetails.data}
                setSelectedBusinessOwner={setSelectedBusinessOwner}
              />
            )}
        </>
      )}

      <div className="mt-5">
        <form
          onSubmit={handleSubmit(onSubmit)}
          ref={formRef}
          className={styles.businessForm}
        >
          <div className="row">
            <div className="col-12">
              <CustomInputField
                type="text"
                label="First Name *"
                name="firstName"
                placeholder="Enter first name"
                reference={register}
                errors={errors.firstName}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Last Name *"
                name="lastName"
                placeholder="Enter last name"
                reference={register}
                errors={errors.lastName}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="email"
                label="Email Address *"
                name="email"
                placeholder="Enter email address"
                reference={register}
                errors={errors.email}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Phone Number *"
                name="phoneNumber"
                placeholder="08000000000"
                reference={register}
                errors={errors.phoneNumber}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="date"
                label="Date of Birth *"
                name="dateOfBirth"
                reference={register}
                errors={errors.dateOfBirth}
                max={getMaxDateOfBirth()}
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Country <span className="text-danger">*</span>{" "}
                  {isLoadingCountries && <small>(Loading...)</small>}
                </label>
                <select
                  name="countryCode"
                  className="form-control"
                  ref={register}
                >
                  <option value="">Select country</option>
                  {countries.map((country: any) => (
                    <option key={country.id} value={country.iso2}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.countryCode} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="National Identity Number *"
                name="identityNumber"
                placeholder="Enter Identity Number"
                reference={register}
                errors={errors.identityNumber}
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  State of Residence <span className="text-danger">*</span>{" "}
                  {isLoadingStates && <small>(Loading...)</small>}
                </label>
                <select
                  name="state"
                  className="form-control"
                  ref={register}
                  disabled={!watchCountry}
                >
                  <option value="">Select state</option>
                  {states.map((state: any) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.state} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="City *"
                name="city"
                placeholder="Enter city"
                reference={register}
                errors={errors.city}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Residential Address *"
                name="address"
                placeholder="Enter your residential address"
                reference={register}
                errors={errors.address}
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Gender <span className="text-danger">*</span>
                </label>
                <select name="gender" className="form-control" ref={register}>
                  <option value="">Select gender</option>
                  {genders.map((gender: any) => (
                    <option key={gender.id} value={gender.name}>
                      {gender.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.gender} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="form-group">
                <label>
                  Academic & Professional Qualifications{" "}
                  <span className="text-danger">*</span>{" "}
                  {isLoadingEducationalQualifications && (
                    <small>(Loading...)</small>
                  )}
                </label>
                <select
                  name="educationalQualification"
                  className="form-control"
                  ref={register}
                >
                  <option value="">Select One</option>
                  {educationalQualifications.map((item: any) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.educationalQualification} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Work Experience *"
                name="workExperience"
                placeholder="e.g 5 years"
                reference={register}
                errors={errors.workExperience}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Experience Relevant to the Role *"
                name="experienceRelevantToTheRole"
                placeholder="e.g 3 years"
                reference={register}
                errors={errors.experienceRelevantToTheRole}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                label="Period in Company Board *"
                name="periodInCompanyBoard"
                placeholder="e.g 7 years"
                reference={register}
                errors={errors.periodInCompanyBoard}
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Identification Type <span className="text-danger">*</span>
                </label>
                <select
                  name="govtIdentityType"
                  className="form-control"
                  ref={register}
                >
                  <option value="">Select identification type</option>
                  {identityTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.govtIdentityType} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Upload ID *"
                name="idDocument"
                placeholder=""
                reference={register}
                errors={errors.idDocument}
                readOnly
              />

              <button
                type="button"
                className="btn btn-sm advancly-btn mb-3"
                onClick={() => idDocumentRef.current?.click()}
              >
                {idDocumentFile ? "Replace" : "Choose File"}
              </button>

              <input
                type="file"
                className="d-none"
                ref={idDocumentRef}
                onChange={(e) => onChangeFile(e, "idDocument")}
                accept="image/png, image/jpg, image/jpeg, application/pdf"
              />
            </div>

            <div className="col-12">
              <div className="form-group">
                <label>
                  Utility Bill Type <span className="text-danger">*</span>{" "}
                  {isLoadingUtilityBillTypes && <small>(Loading...)</small>}
                </label>
                <select
                  name="utilityDocumentType"
                  className="form-control"
                  ref={register}
                >
                  <option value="">Select utility bill type</option>
                  {utilityBillTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <ErrorHandler errors={errors.utilityDocumentType} />
              </div>
            </div>

            <div className="col-12 mt-4">
              <CustomInputField
                type="text"
                label="Proof of Address (e.g. Utility Bill) *"
                name="utilityBill"
                placeholder=""
                reference={register}
                errors={errors.utilityBill}
                readOnly
              />
              <button
                type="button"
                className="btn btn-sm advancly-btn mb-3"
                onClick={() => utilityBillRef.current?.click()}
              >
                {utilityBillFile ? "Replace" : "Choose File"}
              </button>
              <input
                type="file"
                className="d-none"
                ref={utilityBillRef}
                onChange={(e) => onChangeFile(e, "utilityBill")}
                accept="image/png, image/jpg, image/jpeg, application/pdf"
              />
            </div>
          </div>

          <p className={styles.AddBusinessOwner} onClick={handleAddAnother}>
            Add Another Registered Trustee
          </p>

          <div className={styles.ActionButtonContainer}>
            <button
              type="button"
              onClick={handleProceedClick}
              className="btn advancly-btn btn-lg"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? selectedBusinessOwner?.id
                  ? "Updating..."
                  : "Saving..."
                : selectedBusinessOwner?.id
                  ? "Update"
                  : "Save"}
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="btn advancly-btn btn-lg"
            >
              Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRegisteredTrustee;
